import { ExportMenu } from '@/comuns/ExportMenu';
import { TODOS_OS_REGISTROS } from '@/comuns/exportar';
import { dataSimples, moeda, proximoContato } from '@/comuns/formato';
import { dados } from '@/data/porta';
import type { CargaDeContatos, Fornecedor, Lead, MembroDaEquipe, Paciente, PrestadorDeServico } from '@/data/tipos';

const NOME_DA_LISTA: Record<CargaDeContatos['tipo'], string> = {
    paciente: 'pacientes', lead: 'leads', profissional: 'profissionais', fornecedor: 'fornecedores', prestador: 'prestadores de serviço',
};

export interface ExportarContatosProps {
    carga: CargaDeContatos;
    /** Ids marcados. Vazio exporta todos os registros da busca, e não só a página. */
    selecionados: (string | number)[];
    /** A busca da lista, para "todos" querer dizer todos os que a pessoa está vendo. */
    busca?: string;
    label?: string;
    size?: 'sm' | 'md';
}

function titulo(carga: CargaDeContatos): string {
    return `Contatos — ${NOME_DA_LISTA[carga.tipo]}`;
}

/**
 * Exportação da listagem.
 *
 * Componente separado porque a carga é uma união de cinco formatos: dentro de um
 * `ExportMenu` só, as colunas de paciente e de lead colidiriam no mesmo tipo
 * genérico, e a saída passaria a ser conferida por `as` em vez de pelo
 * compilador.
 */
export function ExportarContatos({ carga, selecionados, busca, label, size }: ExportarContatosProps) {
    const marcados = new Set(selecionados.map(String));
    const filtrar = <T extends { id: string }>(itens: T[]) => (marcados.size ? itens.filter((i) => marcados.has(i.id)) : itens);
    const subtitulo = `${marcados.size || carga.total} de ${carga.total} registros`;
    /* Sem marcados, exportava a página (8 linhas) com o subtítulo dizendo o total. */
    const filtro = { busca: busca || undefined, pagina: 1, porPagina: TODOS_OS_REGISTROS };

    if (carga.tipo === 'lead') {
        return (
            <ExportMenu<Lead>
                label={label} size={size}
                carregarTudo={marcados.size ? undefined : async () => {
                    const todos = await dados().contatos('lead', filtro);
                    return todos.tipo === 'lead' ? todos.itens : [];
                }}
                payload={{
                    title: titulo(carga), subtitle: subtitulo, rows: filtrar(carga.itens),
                    columns: [
                        { header: 'Lead', value: (r) => r.nome },
                        { header: 'Telefone', value: (r) => r.telefone },
                        { header: 'Origem', value: (r) => r.origem },
                        { header: 'Interesse', value: (r) => r.interesse },
                        { header: 'Responsável', value: (r) => r.responsavel },
                        { header: 'Próximo contato', value: (r) => proximoContato(r.proximoContatoEm) },
                        { header: 'Etapa', value: (r) => r.etapa },
                        { header: 'Valor estimado', value: (r) => moeda(r.valorEstimadoCentavos) },
                    ],
                }}
            />
        );
    }

    if (carga.tipo === 'profissional') {
        return (
            <ExportMenu<MembroDaEquipe>
                label={label} size={size}
                carregarTudo={marcados.size ? undefined : async () => {
                    const todos = await dados().contatos('profissional', filtro);
                    return todos.tipo === 'profissional' ? todos.itens : [];
                }}
                payload={{
                    title: titulo(carga), subtitle: subtitulo, rows: filtrar(carga.itens),
                    columns: [
                        { header: 'Profissional', value: (r) => r.nome },
                        { header: 'Telefone', value: (r) => r.telefone },
                        { header: 'Função', value: (r) => r.papel },
                        { header: 'Registro', value: (r) => r.registro ?? '—' },
                        { header: 'Especialidades', value: (r) => r.especialidades },
                        { header: 'Carga', value: (r) => r.cargaSemanal ?? '—' },
                        { header: 'Acesso', value: (r) => r.acesso },
                    ],
                }}
            />
        );
    }

    if (carga.tipo === 'fornecedor') {
        return (
            <ExportMenu<Fornecedor>
                label={label} size={size}
                carregarTudo={marcados.size ? undefined : async () => {
                    const todos = await dados().contatos('fornecedor', filtro);
                    return todos.tipo === 'fornecedor' ? todos.itens : [];
                }}
                payload={{
                    title: titulo(carga), subtitle: subtitulo, rows: filtrar(carga.itens),
                    columns: [
                        { header: 'Fornecedor', value: (r) => r.nome },
                        { header: 'CNPJ', value: (r) => r.cnpj ?? '—' },
                        { header: 'Contato', value: (r) => r.contato ?? '—' },
                        { header: 'Telefone', value: (r) => r.telefone ?? '—' },
                        { header: 'E-mail', value: (r) => r.email ?? '—' },
                        { header: 'Prazo', value: (r) => r.prazoEntrega ?? '—' },
                        { header: 'Categoria de conta', value: (r) => r.categoria ?? '—' },
                        { header: 'Situação', value: (r) => (r.ativo ? 'Ativo' : 'Inativo') },
                    ],
                }}
            />
        );
    }

    if (carga.tipo === 'prestador') {
        return (
            <ExportMenu<PrestadorDeServico>
                label={label} size={size}
                carregarTudo={marcados.size ? undefined : async () => {
                    const todos = await dados().contatos('prestador', filtro);
                    return todos.tipo === 'prestador' ? todos.itens : [];
                }}
                payload={{
                    title: titulo(carga), subtitle: subtitulo, rows: filtrar(carga.itens),
                    columns: [
                        { header: 'Prestador', value: (r) => r.nome },
                        { header: 'Tipo de serviço', value: (r) => r.tipoServico ?? '—' },
                        { header: 'Telefone', value: (r) => r.telefone },
                        { header: 'CPF ou CNPJ', value: (r) => r.documento ?? '—' },
                        { header: 'E-mail', value: (r) => r.email ?? '—' },
                        { header: 'Categoria de conta', value: (r) => r.categoria ?? '—' },
                        { header: 'Situação', value: (r) => (r.ativo ? 'Ativo' : 'Inativo') },
                    ],
                }}
            />
        );
    }

    const convenios = new Map(carga.convenios.map((c) => [c.id, c.nome]));
    return (
        <ExportMenu<Paciente>
            label={label} size={size}
            carregarTudo={marcados.size ? undefined : async () => {
                const todos = await dados().contatos('paciente', filtro);
                return todos.tipo === 'paciente' ? todos.itens : [];
            }}
            payload={{
                title: titulo(carga), subtitle: subtitulo, rows: filtrar(carga.itens),
                columns: [
                    { header: 'Paciente', value: (r) => r.nome },
                    { header: 'Telefone', value: (r) => r.telefone },
                    { header: 'Nascimento', value: (r) => dataSimples(r.nascimento) },
                    { header: 'Profissional', value: (r) => r.profissional },
                    { header: 'Último atendimento', value: (r) => dataSimples(r.ultimoAtendimentoEm) },
                    { header: 'Plano', value: (r) => (r.convenioId ? convenios.get(r.convenioId) ?? '—' : '—') },
                    { header: 'Situação', value: (r) => r.situacao },
                    { header: 'Em aberto', value: (r) => moeda(r.emAbertoCentavos) },
                ],
            }}
        />
    );
}
